import { IssueCaseCategory, IssueCaseSubcategory, OrderDoc, OrderProduct } from '@gooduncles/gu-app-schema';
import { Alert, Checkbox, Form, Input, Modal, Select, message } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';
import { IssueModalSource } from 'src/stores/useOrderProductIssueModal';

import { formatNumber } from 'src/utils/common';
import { getOrderProductAlertConf } from 'src/utils/firebase/firebase-short-cut';
import { issueCaseOptions, issueCaseSubOptions } from 'src/utils/issue/issue-util';
import { processOrderProductChange } from 'src/utils/order-product-issue-util';

import classes from './OrderProductIssueModal.module.scss';

/** 시간외 주문 상품 알림 설정 문서 ID */
const TIMEOUT_PRODUCT_ALERT_CONF_ID = 'ZN3PoWWN7j2CERYNNtEA';

export interface OrderProductIssueModalProps {
  open: boolean;
  onClose: () => void;
  order: OrderDoc;
  store: { storeId: string; storeName: string };
  product: OrderProduct;
  changeType: 'volume_change' | 'sold_out';
  newVolume?: number;
  source?: IssueModalSource | null;
  onComplete?: () => void;
}

const OrderProductIssueModal: FC<OrderProductIssueModalProps> = ({
  open,
  onClose,
  order,
  store,
  product,
  changeType,
  newVolume,
  source,
  onComplete,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState<IssueCaseCategory | null>(IssueCaseCategory.ORDER);
  const [subcategory, setSubcategory] = useState<IssueCaseSubcategory | null>(IssueCaseSubcategory.ORDER_ADJUSTMENT);
  const [memo, setMemo] = useState('');
  const [sendKakao, setSendKakao] = useState(true);
  const [useTimeoutTemplate, setUseTimeoutTemplate] = useState(false);

  // 모달이 열릴 때 시간외 상품 여부를 확인하여 기본값 설정
  useEffect(() => {
    if (!open || changeType !== 'sold_out') return;
    getOrderProductAlertConf(TIMEOUT_PRODUCT_ALERT_CONF_ID).then((doc) => {
      if (doc?.productIds?.includes(product.productId)) {
        setUseTimeoutTemplate(true);
      }
    });
  }, [open, changeType, product.productId]);

  const subcategoryOptions = useMemo(() => (category ? issueCaseSubOptions(category) : null), [category]);

  const handleCategoryChange = useCallback((value: IssueCaseCategory) => {
    setCategory(value);
    setSubcategory(null); // 카테고리 변경 시 서브카테고리 초기화
  }, []);

  const handleClose = useCallback(() => {
    setCategory(IssueCaseCategory.ORDER);
    setSubcategory(IssueCaseSubcategory.ORDER_ADJUSTMENT);
    setMemo('');
    setSendKakao(true);
    setUseTimeoutTemplate(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!user) {
      message.error('사용자 정보가 없습니다. 다시 로그인 해주세요.');
      return;
    }

    if (!category) {
      message.error('이슈 카테고리를 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      await processOrderProductChange({
        order,
        store,
        product,
        user,
        category,
        subcategory,
        memo,
        changeType,
        newVolume,
        sendKakaoAlimTalk: sendKakao,
        useTimeoutTemplate: sendKakao && useTimeoutTemplate,
        source: source ?? undefined,
      });

      handleClose();
      onComplete?.();
    } catch {
      // 에러는 processOrderProductChange 내부에서 처리됨
    } finally {
      setLoading(false);
    }
  }, [
    user,
    category,
    subcategory,
    memo,
    sendKakao,
    useTimeoutTemplate,
    order,
    store,
    product,
    changeType,
    newVolume,
    source,
    handleClose,
    onComplete,
  ]);

  const getAlertMessage = () => {
    if (changeType === 'sold_out') {
      return (
        <div className={classes.alertContent}>
          <strong>{product.fullName}</strong>
          <span className={classes.soldOutLabel}>(품절 처리)</span>
        </div>
      );
    }

    return (
      <div className={classes.alertContent}>
        <strong>{product.fullName}</strong>
        <span className={classes.volumeChange}>
          수량: {product.volume} &rarr; {newVolume}
        </span>
        <span className={classes.priceChange}>
          금액: {formatNumber(product.price * product.volume)}원 &rarr; {formatNumber(product.price * (newVolume ?? 0))}
          원
        </span>
      </div>
    );
  };

  return (
    <Modal
      title={changeType === 'sold_out' ? '품절 처리' : '수량 변경'}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText='확인'
      cancelText='취소'
      width={480}
      okButtonProps={{ disabled: !category }}
    >
      <Form layout='vertical' className={classes.form}>
        <Alert type='info' message={getAlertMessage()} className={classes.alert} />

        <Form.Item label='분류' required tooltip='이슈 발생 원인을 분류합니다' className={classes.formItem}>
          <Select
            value={category}
            onChange={handleCategoryChange}
            placeholder='카테고리를 선택하세요'
            options={issueCaseOptions}
            className={classes.select}
          />
        </Form.Item>

        {subcategoryOptions && (
          <Form.Item label='상세 분류' tooltip='상세 분류를 선택합니다 (선택사항)' className={classes.formItem}>
            <Select
              value={subcategory}
              onChange={setSubcategory}
              placeholder='서브카테고리를 선택하세요'
              options={subcategoryOptions}
              className={classes.select}
              allowClear
            />
          </Form.Item>
        )}

        <Form.Item label='메모' tooltip='내부 관리용 메모입니다 (선택사항)' className={classes.formItem}>
          <Input.TextArea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder='이슈에 대한 메모를 입력하세요 (선택)'
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item className={classes.formItem}>
          <Checkbox checked={sendKakao} onChange={(e) => setSendKakao(e.target.checked)}>
            자동 카카오 알림톡 발송
          </Checkbox>
          {changeType === 'sold_out' && sendKakao && (
            <div className={classes.subOption}>
              <Checkbox checked={useTimeoutTemplate} onChange={(e) => setUseTimeoutTemplate(e.target.checked)}>
                시간외 주문건 취소 알림 템플릿 적용
              </Checkbox>
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OrderProductIssueModal;
